import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { profileService } from '@/services/ProfileService';
import { AuthContextType, User } from './authTypes';
import { 
  loginWithEmail, 
  loginWithGoogleProvider, 
  logoutUser 
} from './authUtils';

// Create the context
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
  devModeEnabled: true,
  toggleDevMode: () => {},
  logOut: async () => false,
  login: async () => {},
  loginWithGoogle: async () => {},
  logout: () => {}
});

// Hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Get development mode setting from localStorage or default to true
const getInitialDevMode = () => {
  if (typeof window === 'undefined') return true;
  
  const saved = localStorage.getItem('devModeEnabled');
  return saved !== null ? saved === 'true' : true;
};

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [devModeEnabled, setDevModeEnabled] = useState(getInitialDevMode());

  // Function to toggle development mode
  const toggleDevMode = () => {
    const newDevMode = !devModeEnabled;
    setDevModeEnabled(newDevMode);
    localStorage.setItem('devModeEnabled', String(newDevMode));
    
    // If turning off dev mode, sign out to force regular sign-in UI
    if (!newDevMode) {
      signOut(auth).then(() => {
        console.log('🔒 Signed out from dev account, regular authentication required');
      });
    } else {
      // If turning on dev mode, refresh the page to trigger auto-login
      window.location.reload();
    }
  };

  // For local development, sign in with a real dev account to get Firebase auth token
  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost';
    
    if (isLocalhost && devModeEnabled) {
      // Custom dev account credentials - you can change this email if needed
      const devEmail = 'dev@bonjour-local.com';
      const devPassword = 'dev-password-2024!';
      
      console.log('🔓 Attempting automatic sign-in for local development...');
      
      // Try to sign in with the dev account
      signInWithEmailAndPassword(auth, devEmail, devPassword)
        .then((userCredential) => {
          console.log('🔓 Development user signed in successfully');
          // The auth state change listener will handle user setup
        })
        .catch((error) => {
          if (error.code === 'auth/user-not-found') {
            // If the dev user doesn't exist yet, create it
            console.log('🔓 Creating development user account...');
            createUserWithEmailAndPassword(auth, devEmail, devPassword)
              .then((userCredential) => {
                console.log('🔓 Development user created and signed in');
                // Auth state change listener will handle the rest
              })
              .catch((createError) => {
                console.error('❌ Error creating development user:', createError);
                setIsLoading(false);
              });
          } else if (error.code === 'auth/wrong-password') {
            console.error('❌ Wrong password for development user. If you changed the password, update it in the code.');
            setIsLoading(false);
          } else {
            console.error('❌ Error signing in development user:', error.code, error.message);
            setIsLoading(false);
          }
        });
    }
  }, [devModeEnabled]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const profile = await profileService.getUserProfile(firebaseUser.uid);
          const user: User = {
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: profile?.name || firebaseUser.displayName || 'User',
            profile,
            isAdmin: profile?.admin || false,
          };
          setUser(user);
          setIsAdmin(user.isAdmin);
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Log out function
  const logOut = async (): Promise<boolean> => {
    try {
      await signOut(auth);
      return true;
    } catch (error) {
      console.error('Error signing out:', error);
      return false;
    }
  };

  const login = (email: string, remember: boolean) => {
    return loginWithEmail(email, remember, setIsLoading);
  };

  const loginWithGoogle = (remember: boolean) => {
    return loginWithGoogleProvider(remember, setIsLoading);
  };

  const logout = () => {
    logoutUser();
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isAdmin,
    devModeEnabled,
    toggleDevMode,
    logOut,
    login,
    loginWithGoogle,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
